import React from "react";
import { Plus, Minus, Check } from "lucide-react";
import { useI18n, labelOf } from "@/i18n/I18nProvider";

function ProductCard({ product, qty, onAdd, onInc, onDec }) {
  const { lang, t } = useI18n();
  const discount = product.compare_price && product.compare_price > product.price
    ? Math.round(((product.compare_price - product.price) / product.compare_price) * 100)
    : 0;
  const benefitText = labelOf(product.description, lang, "");
  return (
    <div className="snap-start shrink-0 w-44 sm:w-52 bg-white border border-brand-line rounded-2xl overflow-hidden flex flex-col" data-testid={`rec-product-${product.id}`}>
      <div className="relative aspect-square bg-brand-elevated">
        <img src={product.image_url} alt={labelOf(product.names, lang)} className="w-full h-full object-cover" />
        {discount > 0 && (
          <span className="absolute top-1.5 left-1.5 discount-badge rounded-full !text-[10px] !px-2 !py-0.5">-{discount}%</span>
        )}
      </div>
      <div className="p-2.5 flex flex-col flex-1">
        <p className="font-display text-sm text-brand-dark leading-tight line-clamp-2 font-semibold" style={{ letterSpacing: "-0.01em" }}>{labelOf(product.names, lang)}</p>
        {benefitText && benefitText !== labelOf(product.names, lang) && (
          <p className="font-sans text-[10px] text-brand-muted mt-0.5 line-clamp-1">{benefitText}</p>
        )}
        <div className="mt-1.5 flex items-baseline gap-1.5">
          <span className="font-sans text-sm font-bold text-brand-dark">₹{product.price}</span>
          {product.compare_price > product.price && (
            <span className="font-sans text-[10px] text-brand-muted line-through">₹{product.compare_price}</span>
          )}
        </div>
        <div className="mt-auto pt-2">
          {qty === 0 ? (
            <button
              type="button"
              onClick={onAdd}
              data-testid={`rec-product-add-${product.id}`}
              className="w-full h-8 rounded-full border-2 border-brand-primary text-brand-primary font-semibold text-[10px] uppercase tracking-widest hover:bg-brand-primary hover:text-brand-bone transition-colors flex items-center justify-center gap-1"
            >
              <Plus strokeWidth={2.5} className="w-3 h-3" /> {t("routine.add")}
            </button>
          ) : (
            <div className="flex items-center justify-between bg-brand-primary text-brand-bone rounded-full h-8 px-1.5">
              <button type="button" onClick={onDec} data-testid={`rec-product-dec-${product.id}`} className="w-6 h-6 flex items-center justify-center">
                <Minus strokeWidth={2.5} className="w-3.5 h-3.5" />
              </button>
              <span className="font-sans text-xs font-bold" data-testid={`rec-product-qty-${product.id}`}>{qty}</span>
              <button type="button" onClick={onInc} data-testid={`rec-product-inc-${product.id}`} className="w-6 h-6 flex items-center justify-center">
                <Plus strokeWidth={2.5} className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ComboCard({ combo, isSelected, onToggle }) {
  const { lang, t } = useI18n();
  const discount = combo.compare_price && combo.compare_price > combo.price
    ? Math.round(((combo.compare_price - combo.price) / combo.compare_price) * 100)
    : 0;
  return (
    <div className="snap-start shrink-0 w-60 sm:w-64 bg-white rounded-2xl overflow-hidden border-2 transition-colors flex flex-col" style={{ borderColor: isSelected ? "#C03A2B" : "rgba(15,14,13,0.06)" }} data-testid={`rec-combo-${combo.id}`}>
      <div className="relative aspect-[5/3] bg-brand-line">
        {combo.image_url && <img src={combo.image_url} alt={labelOf(combo.names, lang)} className="w-full h-full object-cover" />}
        <span className="absolute top-2 left-2 bg-brand-dark text-brand-bone text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full">{t("recs.combos").split(" ")[0]}</span>
        {discount > 0 && (
          <span className="absolute top-2 right-2 discount-badge rounded-full !text-[10px] !px-2 !py-0.5">-{discount}%</span>
        )}
      </div>
      <div className="p-3 flex-1 flex flex-col">
        <p className="font-display text-base text-brand-dark font-semibold leading-tight" style={{ letterSpacing: "-0.01em" }}>{labelOf(combo.names, lang)}</p>
        <p className="font-sans text-[10px] text-brand-muted mt-1">{combo.products?.length || 0} {t("review.items").toLowerCase()}</p>
        <div className="mt-1 flex items-baseline gap-1.5">
          <span className="font-sans text-base font-bold text-brand-dark">₹{combo.price}</span>
          {combo.compare_price > combo.price && (
            <span className="font-sans text-[10px] text-brand-muted line-through">₹{combo.compare_price}</span>
          )}
        </div>
        <button
          type="button"
          onClick={onToggle}
          data-testid={`rec-combo-toggle-${combo.id}`}
          className={`mt-2 h-9 rounded-full font-semibold text-[10px] uppercase tracking-widest transition-colors flex items-center justify-center gap-1 ${
            isSelected ? "bg-brand-primary text-brand-bone" : "border-2 border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-brand-bone"
          }`}
        >
          {isSelected ? <><Check strokeWidth={3} className="w-3 h-3" /> {t("routine.added")}</> : t("routine.add")}
        </button>
      </div>
    </div>
  );
}

function HeroBanner({ video_url, t }) {
  return (
    <div className="relative aspect-[16/9] sm:aspect-[21/9] w-full rounded-2xl overflow-hidden bg-brand-dark mb-6" data-testid="recs-video-banner">
      {video_url ? (
        <video src={video_url} autoPlay muted loop playsInline poster="https://images.unsplash.com/photo-1556228720-195a672e8a03?w=1200&q=85" className="w-full h-full object-cover" />
      ) : (
        <img src="https://images.unsplash.com/photo-1556228720-195a672e8a03?w=1200&q=85" alt="" className="w-full h-full object-cover" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/85 via-brand-dark/30 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6 text-brand-bone">
        <p className="font-sans text-[10px] uppercase tracking-[0.3em] font-bold text-brand-tan">{t("brand.name")}</p>
        <h3 className="font-display text-2xl sm:text-3xl font-semibold mt-1 leading-tight" style={{ letterSpacing: "-0.02em" }}>{t("recs.title")}</h3>
      </div>
    </div>
  );
}

export default function RecommendationsStep({ recommendations, items, onChangeItems, t }) {
  const { lang } = useI18n();
  if (!recommendations) {
    return <div className="py-12"><p className="font-sans text-brand-muted">{t("common.loading")}</p></div>;
  }
  const { video_url, products = [], combos = [], collections = [] } = recommendations;

  const isInCart = (id) => items.some((it) => it.product_id === id);
  const qtyOf = (id) => items.find((it) => it.product_id === id)?.qty || 0;
  const isComboSelected = (cid) => items.some((it) => it.product_id === cid && it.kind === "combo");

  const addProduct = (p) => {
    onChangeItems([
      ...items,
      { product_id: p.id, name: labelOf(p.names, lang, "Product"), price: p.price, qty: 1, image_url: p.image_url, kind: "product" },
    ]);
  };
  const setQty = (pid, qty) => {
    if (qty <= 0) {
      onChangeItems(items.filter((it) => it.product_id !== pid));
    } else {
      onChangeItems(items.map((it) => (it.product_id === pid ? { ...it, qty } : it)));
    }
  };

  const toggleCombo = (combo) => {
    if (isComboSelected(combo.id)) {
      onChangeItems(items.filter((it) => !(it.product_id === combo.id && it.kind === "combo")));
    } else {
      onChangeItems([
        ...items,
        { product_id: combo.id, name: labelOf(combo.names, lang, "Combo"), price: combo.price, qty: 1, image_url: combo.image_url, kind: "combo" },
      ]);
    }
  };

  return (
    <div className="pb-28">
      <HeroBanner video_url={video_url} t={t} />

      {combos.length > 0 && (
        <section className="mt-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-xl sm:text-2xl text-brand-dark font-semibold" style={{ letterSpacing: "-0.02em" }}>{t("recs.combos")}</h3>
            <span className="text-[10px] uppercase tracking-widest text-brand-muted font-bold">{combos.length}</span>
          </div>
          <div className="flex gap-2.5 overflow-x-auto snap-x snap-mandatory no-scrollbar -mx-5 px-5 pb-2" data-testid="recs-combos-row">
            {combos.map((c) => (
              <ComboCard key={c.id} combo={c} isSelected={isComboSelected(c.id)} onToggle={() => toggleCombo(c)} />
            ))}
          </div>
        </section>
      )}

      {products.length > 0 && (
        <section className="mt-8">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-xl sm:text-2xl text-brand-dark font-semibold" style={{ letterSpacing: "-0.02em" }}>{t("recs.related")}</h3>
            <span className="text-[10px] uppercase tracking-widest text-brand-muted font-bold">{products.length}</span>
          </div>
          <div className="flex gap-2.5 overflow-x-auto snap-x snap-mandatory no-scrollbar -mx-5 px-5 pb-2" data-testid="recs-products-row">
            {products.map((p) => (
              <ProductCard
                key={p.id}
                product={p}
                qty={qtyOf(p.id)}
                onAdd={() => addProduct(p)}
                onInc={() => setQty(p.id, qtyOf(p.id) + 1)}
                onDec={() => setQty(p.id, qtyOf(p.id) - 1)}
              />
            ))}
          </div>
        </section>
      )}

      {collections.length > 0 && collections.map((coll) => (
        coll.products?.length > 0 && (
          <section key={coll.id} className="mt-8">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-xl sm:text-2xl text-brand-dark font-semibold" style={{ letterSpacing: "-0.02em" }}>{labelOf(coll.names, lang)}</h3>
              <span className="text-[10px] uppercase tracking-widest text-brand-muted font-bold">{coll.products.length}</span>
            </div>
            <div className="flex gap-2.5 overflow-x-auto snap-x snap-mandatory no-scrollbar -mx-5 px-5 pb-2" data-testid={`recs-collection-${coll.id}`}>
              {coll.products.map((p) => (
                <ProductCard
                  key={p.id}
                  product={p}
                  qty={qtyOf(p.id)}
                  onAdd={() => addProduct(p)}
                  onInc={() => setQty(p.id, qtyOf(p.id) + 1)}
                  onDec={() => setQty(p.id, qtyOf(p.id) - 1)}
                />
              ))}
            </div>
          </section>
        )
      ))}

      {products.length === 0 && combos.length === 0 && (
        <p className="font-sans text-sm text-brand-muted mt-12 text-center" data-testid="recs-empty">
          No recommendations yet for the selected concerns. Try another category.
        </p>
      )}
    </div>
  );
}
